// @ts-nocheck - React 18 type incompatibilities with custom components
import React, { useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import clsx from 'clsx';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Text from '@/components/Text';
import Button from '@/components/Button';
import PageContainer from '@/common/PageContainer';
import ChapterAccordion from './ChapterAccordion';
import Heading from '@/common/Heading';
import AssetImage from '@/components/AssetImage';
import GeneratedLessonViewer from '../GeneratedLessonViewer';
import CourseGenerationLoader from '../CourseGenerationLoader';
import { fetchGeneratedCourseSnapshotById, StoredGeneratedCoursePayload, updateGeneratedCourseSnapshot } from '@/utils/supabase';

interface GeneratedCourseProps {
  stockName: string;
  ticker: string;
  isIndustry: boolean;
  onBack: () => void;
  storedCourseId?: string;
  storedCoursePayload?: StoredGeneratedCoursePayload;
  isNewCourse?: boolean;
  onUpdatePayload?: (payload: StoredGeneratedCoursePayload) => void;
  userId?: string;
}

interface Chapter {
  title: string;
  lessons: {
    title: string;
    description: string;
    completed: boolean;
    blocks?: any[];
  }[];
}

interface CourseData {
  title: string;
  chapters: Chapter[];
}

export default function GeneratedCourse({ stockName, ticker, isIndustry, onBack, storedCourseId, storedCoursePayload, isNewCourse, onUpdatePayload, userId }: GeneratedCourseProps) {
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState<CourseData | null>(null);
  const [expandedChapters, setExpandedChapters] = useState<{ [key: number]: boolean }>({ 0: true });
  const [activeLesson, setActiveLesson] = useState<{ chapterIndex: number, lessonIndex: number } | null>(null);
  const [completedLessons, setCompletedLessons] = useState<{ [key: string]: boolean }>({});
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const chapterTriggerTimestamps = useRef<{ [key: number]: number }>({});
  const chapterRetryAttempts = useRef<{ [key: number]: number }>({});
  const hasTriggeredInitialRef = useRef(false);
  const currentPayloadRef = useRef<StoredGeneratedCoursePayload | undefined>(storedCoursePayload);

  // Initialize completed lessons from payload
  useEffect(() => {
    if (storedCoursePayload?.lessonCompletion) {
      const completed: { [key: string]: boolean } = {};
      Object.keys(storedCoursePayload.lessonCompletion).forEach(key => {
        if (storedCoursePayload?.lessonCompletion?.[key]?.completed) {
          completed[key] = true;
        }
      });
      setCompletedLessons(completed);
    }
    currentPayloadRef.current = storedCoursePayload;
  }, [storedCoursePayload]);

  const toggleChapter = (index: number) => {
    setExpandedChapters(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Helper to map payload to courseData
  const mapPayloadToCourseData = (payload: StoredGeneratedCoursePayload): CourseData => {
    const outline = payload.outline;
    return {
        title: outline.title,
        chapters: outline.chapters.map((ch: any) => ({
          title: ch.title,
          lessons: ch.lessons.map((l: any) => ({
            title: l.title,
            description: l.description,
            completed: false,
            blocks: l.items || l.blocks
          }))
        }))
    };
  };

  // 1. Initial Load: from stored payload or Supabase
  useEffect(() => {
    const loadCourseData = async () => {
      // Try Supabase first if we have an ID
      if (storedCourseId) {
        try {
          const snapshot = await fetchGeneratedCourseSnapshotById(storedCourseId, userId);
          if (snapshot?.course_payload?.outline) {
            const payload = snapshot.course_payload as StoredGeneratedCoursePayload;
            setCourseData(mapPayloadToCourseData(payload));
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading course from Supabase:', error);
        }
      }
      
      // Fallback to prop payload
      if (storedCoursePayload?.outline) {
        setCourseData(mapPayloadToCourseData(storedCoursePayload));
        if (!isNewCourse) setLoading(false);
      } else {
        setLoading(false);
      }
    };

    loadCourseData();
  }, [storedCourseId]); // Only run once when course ID is set

  // 2. Generation Trigger: once per course ID
  useEffect(() => {
    if (storedCourseId && courseData && !hasTriggeredInitialRef.current) {
      hasTriggeredInitialRef.current = true;
      const chapters = courseData.chapters;
      chapters.forEach((ch, idx) => {
        if (!ch.lessons || ch.lessons.length === 0) {
          console.log(`🚀 Triggering background generation for chapter ${idx}`);
          chapterTriggerTimestamps.current[idx] = Date.now();
          triggerChapterGeneration(storedCourseId, idx);
        }
      });
    }
  }, [storedCourseId, !!courseData]);

  // 3. Polling Logic
  const pollForUpdates = async (id: string) => {
    try {
      const snapshot = await fetchGeneratedCourseSnapshotById(id, userId);
      if (!snapshot?.course_payload) return;

      const payload = snapshot.course_payload as StoredGeneratedCoursePayload;
      const newOutline = payload.outline;

      if (newOutline?.chapters) {
        const newData = mapPayloadToCourseData(payload);
        
        // Only update if something changed (more lessons)
        const currentLessonsCount = courseData?.chapters.reduce((acc, ch) => acc + ch.lessons.length, 0) || 0;
        const newLessonsCount = newData.chapters.reduce((acc, ch) => acc + ch.lessons.length, 0);
        
        if (newLessonsCount > currentLessonsCount) {
          console.log(`✨ Polling found new content: ${newLessonsCount} lessons total.`);
          setCourseData(newData);
          onUpdatePayload?.(payload); // Keep parent in sync
        }

        const allChaptersDone = newOutline.chapters.every((ch: any) => ch.lessons && ch.lessons.length > 0);
        if (allChaptersDone) {
          console.log('✅ All chapters generated. Stopping polling.');
          stopPolling();
          return;
        }
      }

      // Retry logic for stuck chapters
      const now = Date.now();
      const TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
      const MAX_RETRIES = 2;

      newOutline.chapters.forEach((chapter: any, index: number) => {
        if ((!chapter.lessons || chapter.lessons.length === 0) && chapterTriggerTimestamps.current[index]) {
          const timeSinceTrigger = now - chapterTriggerTimestamps.current[index];
          const retryCount = chapterRetryAttempts.current[index] || 0;

          if (timeSinceTrigger > TIMEOUT_MS && retryCount < MAX_RETRIES) {
            console.log(`⚠️ Chapter ${index} stuck, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
            chapterRetryAttempts.current[index] = retryCount + 1;
            chapterTriggerTimestamps.current[index] = now;
            triggerChapterGeneration(id, index);
          }
        }
      });
    } catch (error) {
      console.error('Error polling:', error);
    }
  };

  const startPolling = (id: string) => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = setInterval(() => pollForUpdates(id), 5000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const triggerChapterGeneration = (snapshotId: string, chapterIndex: number) => {
    const edgeFunctionUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/generate_remaining_chapters_web`;

    fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ snapshotId, chapterIndex }),
    }).catch(console.error);
  };

  // Polling control based on completeness
  useEffect(() => {
    if (storedCourseId && courseData) {
      const hasIncomplete = courseData.chapters.some(ch => !ch.lessons || ch.lessons.length === 0);
      if (hasIncomplete) {
      startPolling(storedCourseId);
      } else {
        stopPolling();
      }
    }
    return () => stopPolling();
  }, [storedCourseId, !!courseData, courseData?.chapters.some(ch => ch.lessons.length > 0)]);

  const handleStartLesson = (chapterIndex: number, lessonIndex: number) => {
    const lesson = courseData?.chapters[chapterIndex].lessons[lessonIndex];
    if (!lesson?.blocks || lesson.blocks.length === 0) {
      alert('Lesson content not ready yet. Please wait.');
      return;
    }
    setActiveLesson({ chapterIndex, lessonIndex });
  };

  const handleCompleteLesson = () => {
    if (activeLesson && courseData) {
      const { chapterIndex, lessonIndex } = activeLesson;
      const key = `${chapterIndex}-${lessonIndex}`;
      setCompletedLessons(prev => ({ ...prev, [key]: true }));
      setActiveLesson(null);
      
      // Update completion in Supabase
      if (storedCourseId && currentPayloadRef.current) {
        const updatedPayload: StoredGeneratedCoursePayload = {
          ...currentPayloadRef.current,
          lessonCompletion: {
            ...currentPayloadRef.current.lessonCompletion,
            [key]: { completed: true, completedAt: new Date().toISOString() }
          }
        };
        currentPayloadRef.current = updatedPayload;
        updateGeneratedCourseSnapshot({ id: storedCourseId, coursePayload: updatedPayload });
        onUpdatePayload?.(updatedPayload);
      }
    }
  };

  const getCompletedCount = (chapterIndex: number) => {
    if (!courseData) return 0;
    return courseData.chapters[chapterIndex].lessons.filter((_, idx) => completedLessons[`${chapterIndex}-${idx}`]).length;
  };

  const isGenerating = isNewCourse && courseData && courseData.chapters.every(ch => !ch.lessons || ch.lessons.length === 0);

  return (
    <PageContainer className="pb-24">
      <Head>
        <title>{courseData?.title || 'Your Course'} | Bloom</title>
      </Head>

      {activeLesson && courseData && courseData.chapters[activeLesson.chapterIndex]?.lessons[activeLesson.lessonIndex] ? (
        <GeneratedLessonViewer
          lessonTitle={courseData.chapters[activeLesson.chapterIndex].lessons[activeLesson.lessonIndex].title}
          lessonItems={courseData.chapters[activeLesson.chapterIndex].lessons[activeLesson.lessonIndex].blocks}
          stockName={stockName}
          ticker={ticker}
          isIndustry={isIndustry}
          chapterNumber={activeLesson.chapterIndex + 1}
          onClose={() => setActiveLesson(null)}
          onComplete={handleCompleteLesson}
          isFinalLesson={
            activeLesson.chapterIndex === courseData.chapters.length - 1 &&
            activeLesson.lessonIndex === courseData.chapters[activeLesson.chapterIndex].lessons.length - 1
          }
        />
      ) : (
        <div className="flex flex-col gap-8 w-full">
        {/* Header Section */}
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2 md:gap-4">
            <Button type="IconButton" className="bg-btn-primary flex-none !w-8 !h-8 md:!w-10 md:!h-10 !p-0 flex items-center justify-center" onClick={onBack}>
              <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 text-white flex-shrink-0" />
            </Button>
            <div className="flex items-center gap-3">
              {!isIndustry && ticker && (
                <AssetImage alt={ticker} src="" width={48} height={48} className="w-12 h-12 rounded-xl bg-white/10" />
              )}
              <Text size="md" className="text-white">{stockName}</Text>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h1 className="text-md md:text-lg text-white leading-tight">
              {courseData?.title || 'Loading your course...'}
            </h1>
            {courseData && (
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30">
                  <Text size="xs" className="text-blue-400">
                    {isIndustry ? 'Industry Course' : 'Stock Deep Dive'}
                  </Text>
                </div>
                <Text size="sm" className="text-white/40">•</Text>
                <Text size="sm" className="text-white/60">
                  {courseData.chapters.length} Chapters
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Course Content */}
        <div className="flex flex-col gap-2">
          {!courseData ? (
            <div className="flex flex-col gap-4 animate-pulse">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-white/5 border border-white/10 rounded-2xl" />
              ))}
            </div>
          ) : (
            courseData.chapters.map((chapter, index) => (
              <div 
                key={index} 
                className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <ChapterAccordion
                  chapterNumber={index + 1}
                  chapterTitle={chapter.title}
                  lessons={chapter.lessons.map((l, idx) => ({ ...l, completed: !!completedLessons[`${index}-${idx}`] }))}
                  isExpanded={!!expandedChapters[index]}
                  onToggle={() => toggleChapter(index)}
                  onStartLesson={(lessonIdx) => handleStartLesson(index, lessonIdx)}
                  completedLessonsCount={getCompletedCount(index)}
                  isLoading={chapter.lessons.length === 0}
                />
              </div>
            ))
          )}
        </div>
        </div>
      )}

      {/* Generation Notification */}
      {isGenerating && !loading && !activeLesson && (
        <div className="fixed bottom-6 left-6 right-6 md:left-auto md:right-8 md:w-80 z-50 animate-in slide-in-from-bottom-4 duration-500">
          <div className="bg-darkglass border border-white/10 backdrop-blur-xl p-4 rounded-2xl shadow-2xl flex items-center gap-4">
            <div className="flex-none w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
            <div className="flex flex-col">
              <Text size="sm" className="text-white font-medium">Building your course...</Text>
              <Text size="xs" className="text-white/50">This usually takes up to 1 minute.</Text>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
